import { MailIcon, MessageSquareTextIcon, NotebookPenIcon, PhoneIcon } from 'lucide-react';

import type { SupportSource } from '@/_containers/support/shared/schemas/support-query-schema';
import { sourceLabels } from '@/_containers/support/shared/schemas/support-query-schema';
import type { FileQuerySchema } from '@/_schemas/file';

import OutGoingEmail from './outgoing-email.svg?react';

// // Remove all inline style attributes from an HTML string safely
// export function stripInlineStyles(html: string): string {
//   if (!html) return '';
//   // return html;

//   // If DOM is not available (e.g., SSR), fall back to a regex-based best effort
//   if (typeof document === 'undefined') {
//     return html.replace(/\s*style\s*=\s*("[^"]*"|'[^']*')/gi, '');
//   }

//   const container = document.createElement('div');
//   container.innerHTML = html;
//   container.querySelectorAll('[style]').forEach((el) => el.removeAttribute('style'));
//   return container.innerHTML;
// }

// Helper to remove only the `color` declaration from a CSS style string, preserving others
function removeColorDeclarations(style: string): string {
  if (!style) return '';
  const parts = style
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);
  const kept = parts.filter((p) => {
    const idx = p.indexOf(':');
    if (idx === -1) return true; // keep malformed fragments as-is
    const prop = p.slice(0, idx).trim().toLowerCase();
    return prop !== 'color';
  });
  return kept.join('; ');
}

// Helper to add max-width: 100% and object-fit: contain to img tag styles
function addImgStyles(style: string): string {
  if (!style) return 'max-width: 100%; object-fit: contain';

  const parts = style
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);

  // Check if max-width or object-fit are already present and remove them
  const filteredParts = parts.filter((p) => {
    const idx = p.indexOf(':');
    if (idx === -1) return true;
    const prop = p.slice(0, idx).trim().toLowerCase();
    return prop !== 'max-width' && prop !== 'object-fit';
  });

  // Add max-width: 100% and object-fit: contain
  filteredParts.push('max-width: 100%', 'object-fit: contain');
  return filteredParts.join('; ');
}

// Remove all inline style attributes from an HTML string safely
export function stripInlineStylesColor(html: string): string {
  if (!html) return '';
  // return html;

  // If DOM is not available (e.g., SSR), fall back to a regex-based best effort
  if (typeof document === 'undefined') {
    let processedHtml = html;

    // Process existing style attributes to remove color
    processedHtml = processedHtml.replace(/style\s*=\s*("([^"]*)"|'([^']*)')/gi, (_m, _q, dq, sq) => {
      const content = (dq ?? sq ?? '') as string;
      const newContent = removeColorDeclarations(content);
      if (!newContent) return '';
      const quote = dq !== undefined ? '"' : "'";
      return `style=${quote}${newContent}${quote}`;
    });

    // Add max-width: 100% and object-fit: contain to img tags
    processedHtml = processedHtml.replace(
      /<img([^>]*?)(?:\s+style\s*=\s*("([^"]*)"|'([^']*)'))?([^>]*?)>/gi,
      (_match, before, _q, dq, sq, after) => {
        const existingStyle = (dq ?? sq ?? '') as string;
        const newStyle = addImgStyles(existingStyle);
        const quote = dq !== undefined ? '"' : sq !== undefined ? "'" : '"';
        return `<img${before} style=${quote}${newStyle}${quote}${after}>`;
      }
    );

    return processedHtml;
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  // Remove color from existing styled elements
  container.querySelectorAll('[style]').forEach((el) => {
    (el as HTMLElement).style.removeProperty('color');
    const current = el.getAttribute('style');
    if (!current || current.trim() === '') {
      el.removeAttribute('style');
    }
  });

  // Add max-width: 100% and object-fit: contain to all img tags
  container.querySelectorAll('img').forEach((img) => {
    const imgElement = img as HTMLImageElement;
    imgElement.style.maxWidth = '100%';
    imgElement.style.objectFit = 'contain';
  });

  return container.innerHTML;
}

export function ticketTypeMapper(t: SupportSource) {
  const label = sourceLabels[t] || '***UNKNOWN***';
  switch (t.toString()) {
    case '1':
      return { label, icon: MailIcon, helperLabel: 'emailed' };
    case '2':
      return { label, icon: PhoneIcon, helperLabel: 'opened' };
    case '3':
      return { label, icon: PhoneIcon, helperLabel: 'called' };
    case '7':
      return { label, icon: MessageSquareTextIcon, helperLabel: 'messaged' };
    case '9':
      return { label, icon: NotebookPenIcon, helperLabel: 'messaged' };
    case '10':
      return { label, icon: OutGoingEmail, helperLabel: 'emailed' };
    default:
      return { label, icon: MailIcon, helperLabel: 'emailed' };
  }
}

export function freshDeskAttachmentToFile(attachment: {
  id: number;
  name: string;
  content_type: string;
  size: number;
  created_at: string;
  updated_at: string;
  attachment_url: string;
}): FileQuerySchema {
  return {
    s3_path: '',
    folder_path: '',
    guid: '',
    etag: '' as string | null | undefined,
    access_level: '',

    id: attachment.id,
    created_at: attachment.created_at,
    updated_at: attachment.updated_at,
    name: attachment.name,
    url: attachment.attachment_url,
    mime_type: 'application/octet-stream',
    file_size: 1348,
  };
}
